import { EXCLUSION_LABEL, EXCLUSION_REASONS, LOCATIONS, LOCATION_LABEL, fmt, type CalcResult, type ExclusionReason, type Location, type Scenario } from "@h2lca/core";
import { Checkbox, Select, TextField } from "@/components/ui/FormControls";
import { useWorkshop } from "@/store/workshop";
import { useMaster } from "@/store/master";
import { IssueLine } from "../IssueList";
import { StageBadge } from "../Badges";
import { GlossaryTip } from "../GlossaryTip";
import { Section } from "../Section";

/** ④ 시스템 경계 설정 , 설비별 포함,제외와 사유, 위치 */
export function BoundaryStep({ scenario, result }: { scenario: Scenario; result: CalcResult }) {
  const setBoundary = useWorkshop((s) => s.setBoundary);
  const updateEquipment = useWorkshop((s) => s.updateEquipment);
  const cert = scenario.method === "cert";
  const locked = useMaster((s) => s.data.methods.find((m) => m.id === scenario.method)?.boundary.locked) ?? false;
  const ordered = [...scenario.equipment].sort((a, b) => a.order - b.order);
  const inCount = result.byEquipment.filter((e) => e.inBoundary).length;

  return (
    <Section
      title={
        <>
          시스템 경계 설정 <GlossaryTip k="system_boundary" />
        </>
      }
      desc="어떤 설비를 계산에 넣고 뺄지 정합니다. 제외한 설비는 화면에 남기고 사유를 기록합니다."
      aside={
        <span className="text-footnote text-ink-subtle">
          포함 <b className="font-mono text-ink">{inCount}</b>, 제외 <b className="font-mono text-ink">{ordered.length - inCount}</b>
        </span>
      }
    >
      <p className="text-footnote text-ink-subtle">
        제외기준(컷오프) <GlossaryTip k="cutoff" />, 단위공정 <GlossaryTip k="unit_process" />, 인증제는 원료 조달부터 생산시설 출하까지만 산정합니다 <GlossaryTip k="well_to_gate" />
      </p>
      {ordered.length === 0 && <p className="note note-warn text-footnote">설비가 없습니다. 설비 구성 단계에서 먼저 설비를 놓으세요.</p>}
      {locked && <p className="note note-warn text-footnote">이 산정방식의 경계는 잠겨 있습니다. 경계 안 설비는 개별 제외할 수 없으며, 이전 파일에서 제외된 설비는 다시 포함해야 최종 결과를 산출합니다.</p>}
      {result.issues.filter((i) => i.step === "boundary" && i.level !== "info").map((issue, i) => <IssueLine key={i} issue={issue} link={false} />)}
      <ul className="flex flex-col gap-item">
        {ordered.map((e) => {
          const r = result.byEquipment.find((x) => x.equipmentId === e.id);
          const forced = r?.forcedOut ?? false;
          const included = r?.inBoundary ?? e.inBoundary;
          return (
            <li key={e.id} className={`rounded-card border p-content ${included ? "border-line-soft bg-panel" : "border-line-soft bg-surface-inset"}`}>
              <div className="flex flex-wrap items-start gap-content">
                <div className="min-w-0 flex-1">
                  <Checkbox
                    label={
                      <span className="flex items-center gap-inline">
                        <span className="font-mono text-ink-subtle">{e.order + 1}.</span>
                        <StageBadge stage={e.stage} />
                        <span className={`font-semibold ${included ? "text-ink" : "text-ink-muted"}`}>{e.name}</span>
                      </span>
                    }
                    checked={included}
                    disabled={forced || (locked && included)}
                    onChange={(ev) => setBoundary(e.id, ev.target.checked)}
                    aside={
                      r && (
                        <span className="font-mono text-footnote text-ink-muted">
                          {included ? `${fmt(r.kgCO2eq)} kgCO2eq` : "제외"}
                        </span>
                      )
                    }
                    description={
                      forced ? (
                        <span className="badge-soft badge-boundary">인증제 산정범위 밖. 출하 이후 설비</span>
                      ) : included ? (
                        locked ? "계산에 포함 · 산정 경계 고정" : "계산에 포함"
                      ) : (
                        `제외 사유: ${EXCLUSION_LABEL[e.exclusionReason ?? "outside_boundary"]}`
                      )
                    }
                  />
                  {forced && <p className="mt-label text-footnote text-ink-subtle">인증제 모의산정은 생산시설 출하 이후의 저장 및 이송을 결과에서 제외합니다. 일반 LCA로 바꾸면 다시 포함됩니다.</p>}
                </div>
                <div className="flex w-full flex-col gap-item sm:w-auto sm:min-w-field">
                  {!forced && !included && (
                    <>
                      <label className="control-field">
                        <span className="control-label">제외 사유</span>
                        <Select value={e.exclusionReason ?? "outside_boundary"} onChange={(ev) => setBoundary(e.id, false, ev.target.value as ExclusionReason, e.exclusionNote)}>
                          {EXCLUSION_REASONS.filter((x) => x !== "cert_scope").map((x) => (
                            <option key={x} value={x}>
                              {EXCLUSION_LABEL[x]}
                            </option>
                          ))}
                        </Select>
                      </label>
                      <TextField label="사유 메모" value={e.exclusionNote ?? ""} onChange={(ev) => setBoundary(e.id, false, e.exclusionReason, ev.target.value)} placeholder="예: 전체 배출의 1% 미만" />
                    </>
                  )}
                  {cert ? (
                    <p className="text-footnote text-ink-subtle">
                      위치: <b className="text-ink">{LOCATION_LABEL[e.location]}</b>
                    </p>
                  ) : (
                    <label className="control-field">
                      <span className="control-label">설비 위치</span>
                      <Select value={e.location} onChange={(ev) => updateEquipment(e.id, { location: ev.target.value as Location })}>
                        {LOCATIONS.map((l) => (
                          <option key={l} value={l}>
                            {LOCATION_LABEL[l]}
                          </option>
                        ))}
                      </Select>
                    </label>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </Section>
  );
}
